import { useStoreDispatch, useStoreSelector } from 'hooks/store.ts'
import { useMemo, useState } from 'react'
import { EditMeterReadingsFormInputs, MeterReadingInputLayoutContract } from './types.ts'
import { MeterReading, MeterReadingsPayload } from 'types/contracts.ts'
import { ConfirmationModalProps } from './components/confirmation-modal/types.ts'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { FormProvider, useForm } from 'react-hook-form'
import { determineElMeterReadingInputLayoutContract, exceedsCap, generateCreateMeterReadingRequest, hasLowerValue } from './utils.ts'
import { isActiveContract } from 'utils/contracts.ts'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import { saveMeterReadings } from 'lib/contracts.ts'
import { ProductType } from 'types/types.ts'
import styles from './EditMeterReadings.module.scss'
import { Banner, Button } from '@boltenergy-be/design-system'
import ConfirmationModal from './components/confirmation-modal/ConfirmationModal.tsx'
import { Navigate, useNavigate, useSearchParams } from 'react-router'
import MeterReadingCard from './components/meter-reading-card/MeterReadingCard.tsx'
import { boltApi } from 'store/queries/bolt-api'
import { TagTypes } from 'store/queries/bolt-api/types.ts'
import { ISO8601_DATE_FORMAT } from 'constants/constants.ts'
import DateField from 'components/form-fields/date-field/DateField.tsx'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import { routes } from 'types/routes.ts'
import useGetCurrentContractMeterReadings from 'hooks/useGetCurrentContractMeterReadings.ts'
import { DIRECTIONS, ENERGY_TYPES } from '@boltenergy-be/shared'
import { EDIT_METER_READINGS_URL_PARMS } from 'pages/app/consumption/edit-meter-readings/constants.ts'
import ConfirmationView from './components/confirmation-view/ConfirmationView.tsx'
import { IS_PROD_TESTING } from 'constants/envs.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'
import { hasEnergyTypeValue } from 'store/access-rights/utils.ts'
import EmptyState from 'components/return-later/EmptyState.tsx'
import { BOLT_BACKEND_TAGS } from 'store/queries/bolt-backend/constants.ts'

const EditMeterReadings = () => {
  // Redux store
  const { electricity, gas } = useStoreSelector(selectCurrentServiceContracts)
  const dispatch = useStoreDispatch()

  // Meter readings
  const { data: meterReadingsData } = useGetCurrentContractMeterReadings()

  // Local state
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [meterReadingsPayload, setMeterReadingsPayload] = useState<MeterReadingsPayload>()
  const [confirmationState, setConfirmationState] = useState<ConfirmationModalProps['state']>('LOW')
  const [view, setView] = useState<'edit' | 'confirmation'>('edit')

  // i18n
  const { t } = useTranslation(['consumption', 'common'])

  // Router
  const navigate = useNavigate()
  const [query] = useSearchParams()

  // Access rights
  const { canAccess, showContent } = useGetAccessRight(ACCESS_RIGHT_KEYS.meterReadings)
  const canEditElectricity = canAccess.edit[ENERGY_TYPES.electricity]
  const canEditGas = canAccess.edit[ENERGY_TYPES.gas]

  // DayJS
  const now = dayjs()

  // React Hook Form
  const hookForm = useForm<EditMeterReadingsFormInputs>({
    mode: 'onBlur',
    defaultValues: {
      date: now.format(ISO8601_DATE_FORMAT)
    }
  })
  const { handleSubmit } = hookForm

  // Memos
  const { allElOptional, electricity: electricityInputLayoutContract } = useMemo<MeterReadingInputLayoutContract>(
    () => determineElMeterReadingInputLayoutContract(electricity, meterReadingsData),
    [meterReadingsData, electricity]
  )

  // Constants
  const hasEffectiveGasContract = gas && isActiveContract(gas)

  /**
   * Handles the billshock call after confirmation
   *
   * @param {MeterReadingsPayload} meterReadingsPayload
   */
  const handleConfirm = async (meterReadingsPayload: MeterReadingsPayload) => {
    setLoading(true)

    if (IS_PROD_TESTING) {
      alert(`Should update meter readings (not really, since this is prod testing)`)
      setLoading(false)
      setError(false)
      return
    }

    const date = meterReadingsPayload.date
    const [electricitySuccess, gasSuccess]: (boolean | undefined)[] = await Promise.all([
      meterReadingsPayload.electricity
        ? saveMeterReadings(
            generateCreateMeterReadingRequest(date, electricity, meterReadingsPayload.electricity, ENERGY_TYPES.electricity)
          )
        : undefined,
      hasEffectiveGasContract && meterReadingsPayload.gas
        ? saveMeterReadings(generateCreateMeterReadingRequest(date, gas, meterReadingsPayload.gas, ENERGY_TYPES.gas))
        : undefined
    ])

    const elContinue = typeof electricitySuccess === 'boolean' ? electricitySuccess : true
    const gasContinue = hasEffectiveGasContract && typeof gasSuccess === 'boolean' ? gasSuccess : true
    if (elContinue && gasContinue) {
      const skipConfirmationView = query.has(EDIT_METER_READINGS_URL_PARMS.skipConfirmationView)

      if (skipConfirmationView) {
        // Close modal & navigate back
        setIsConfirmationModalOpen(false)
        void navigate(-1)
      } else {
        window.scrollTo({ top: 0 })
        setIsConfirmationModalOpen(false)
        setView('confirmation')
      }

      if (
        meterReadingsData?.electricity?.date &&
        dayjs(meterReadingsPayload?.date, 'day').isSameOrAfter(dayjs(meterReadingsData.electricity.date, 'day'))
      ) {
        dispatch(boltApi.util.invalidateTags([BOLT_BACKEND_TAGS.billShock, TagTypes.CONSUMPTION, TagTypes.METER_READINGS]))
        trackEvent({ category: 'meter_readings', object: 'edit', action: 'confirm' })
      }
    } else {
      setLoading(false)
      setError(true)
    }
  }

  /**
   * Handles the form submit after validation by React Hook Form
   *
   * @param {EditMeterReadingsFormInputs} data
   */
  const onSubmit = (data: EditMeterReadingsFormInputs) => {
    const meterReadingsPayload = {
      date: data.date,
      electricity: canEditElectricity
        ? {
            ean: electricity.deliveryPoint.ean,
            consumption: {
              singleRate: data.electricity?.consumption?.total ?? undefined,
              exclNight: data.electricity?.consumption?.exclNight ?? undefined,
              ...((typeof data.electricity?.consumption?.day !== 'undefined' ||
                typeof data.electricity.consumption?.night !== 'undefined') && {
                doubleRate: {
                  day: data.electricity?.consumption?.day ?? undefined,
                  night: data.electricity?.consumption?.night ?? undefined
                }
              })
            } as MeterReading,
            ...((typeof data.electricity.injection?.total !== 'undefined' ||
              typeof data.electricity.injection?.day !== 'undefined' ||
              typeof data.electricity.injection?.night !== 'undefined') && {
              injection: {
                singleRate: data.electricity.injection?.total ?? undefined,
                doubleRate: {
                  day: data.electricity.injection?.day ?? undefined,
                  night: data.electricity.injection?.night ?? undefined
                }
              }
            })
          }
        : undefined,
      ...(hasEffectiveGasContract &&
        canEditGas && {
          gas: {
            ean: gas.deliveryPoint.ean,
            consumption: {
              singleRate: data.gas?.total ?? undefined
            }
          }
        })
    }
    setMeterReadingsPayload(meterReadingsPayload)

    const hasLower = hasLowerValue(meterReadingsPayload, meterReadingsData, canAccess.edit)
    const hasHigher = exceedsCap(meterReadingsPayload, meterReadingsData)

    // Check if at least one value is lower than previous entry to show confirmation modal
    if (meterReadingsData && (hasLower || hasHigher)) {
      setIsConfirmationModalOpen(true)
      setConfirmationState(hasHigher && hasLower ? 'BOTH' : hasLower ? 'LOW' : 'HIGH')
    } else {
      // Proceed without confirmation
      void handleConfirm(meterReadingsPayload)
    }
  }

  // EARLY RETURN - Navigate away from edit page if inaccessible
  if (!hasEnergyTypeValue(canAccess.edit)) {
    return <Navigate to={hasEnergyTypeValue(canAccess.view) ? routes.CONSUMPTION_METER_READINGS : routes.CONSUMPTION} replace />
  }

  // EARLY RETURN - Show return later state when not active and not older than a month
  if (!showContent) {
    return <EmptyState description={t(($) => $.meterReadings.returnLater)} />
  }

  if (view === 'confirmation') {
    return <ConfirmationView />
  }

  return (
    <>
      <FormProvider {...hookForm}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <DateField label={t(($) => $.editMeterReadings.form.date)} max={now.format(ISO8601_DATE_FORMAT)} />

          {/* ELECTRICITY INPUTS */}
          {canEditElectricity && electricityInputLayoutContract.consumption && (
            <MeterReadingCard
              type={ProductType.ELECTRICITY}
              direction={DIRECTIONS.consumption}
              inputLayout={electricityInputLayoutContract}
              {...{ allElOptional }}
            />
          )}

          {/* INJECTION INPUTS */}
          {canEditElectricity && electricityInputLayoutContract.injection && (
            <MeterReadingCard
              type={ProductType.ELECTRICITY}
              direction={DIRECTIONS.production}
              inputLayout={electricityInputLayoutContract}
              {...{ allElOptional }}
            />
          )}

          {/* GAS INPUTS */}
          {canEditGas && hasEffectiveGasContract && (
            <MeterReadingCard
              type={ProductType.GAS}
              direction={DIRECTIONS.consumption}
              inputLayout={electricityInputLayoutContract}
              {...{ allElOptional }}
            />
          )}

          {error && (
            <Banner type="blocking">
              <span>
                {t(($) => $.formFeedback.error, {
                  ns: 'common'
                })}
              </span>
            </Banner>
          )}

          <div className={styles.actions}>
            <Button size="large" isFullwidth loading={loading} disabled={IS_PROD_TESTING}>
              {t(($) => $.confirm, {
                ns: 'common'
              })}
            </Button>

            <Button type="button" size="large" isFullwidth variant="secondary" onClick={() => navigate(-1)}>
              {t(($) => $.cancel, {
                ns: 'common'
              })}
            </Button>
          </div>
        </form>
      </FormProvider>

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        state={confirmationState}
        setClose={() => setIsConfirmationModalOpen(false)}
        onSubmit={() => meterReadingsPayload && handleConfirm(meterReadingsPayload)}
      />
    </>
  )
}

export default EditMeterReadings
